<template>
  <div>
    <form-section :title="$t('Integrations_Settings_BasicSettings')">
      <rest-form-item
        :label="$t('Integrations_Field_Name')"
        :hint="$t('Integrations_Field_Name_Description')"
        prop="name"
      >
        <el-input
          v-model="model.name"
          :placeholder="$t('Integrations_Field_Name')"
        />
      </rest-form-item>
    </form-section>

    <form-section :title="$t('Integrations_Settings_ConnectionSettings')">
      <rest-form-item
        :label="$t('Integrations_S3_Field_AuthMode')"
        prop="params.authType"
      >
        <enum-select
          v-model="model.params.authType"
          :change="onChangeType()"
          :options="S3AuthenticationTypesLabels"
        />
      </rest-form-item>

      <el-row v-if="model.params.authType === 'basic'">
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_S3_Field_AccessKey')"
            prop="params.accessKey"
          >
            <el-input
              v-model="model.params.accessKey"
              :placeholder="$t('Integrations_S3_Field_AccessKey')"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_S3_Field_SecretKey')"
            prop="params.secretKey"
          >
            <el-input
              v-model="model.params.secretKey"
              :placeholder="$t('Integrations_S3_Field_SecretKey')"
            />
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row v-if="model.params.authType === 'assume_role'">
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_S3_Field_RoleARN')"
            prop="params.roleArn"
          >
            <el-input
              v-model="model.params.roleArn"
              :placeholder="$t('Integrations_S3_Field_RoleARN')"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="6">
          <rest-form-item
            :label="$t('Integrations_S3_Field_RoleSessionName')"
            prop="params.roleSessionName"
          >
            <el-input
              v-model="model.params.roleSessionName"
              :placeholder="$t('Integrations_S3_Field_RoleSessionName')"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="6">
          <rest-form-item
            :label="$t('Integrations_S3_Field_RoleExternalId')"
            prop="params.roleExternalId"
          >
            <el-input
              v-model="model.params.roleExternalId"
              :placeholder="$t('Integrations_S3_Field_RoleExternalId')"
            />
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_S3_Field_Region')"
            prop="params.region"
          >
            <el-input
              v-model="model.params.region"
              :placeholder="$t('Integrations_S3_Field_Region')"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_S3_Field_Bucket')"
            prop="params.bucket"
          >
            <el-input
              v-model="model.params.bucket"
              :placeholder="$t('Integrations_S3_Field_Bucket')"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_S3_Field_Prefix')"
            prop="params.prefix"
          >
            <el-input
              v-model="model.params.prefix"
              :placeholder="$t('Integrations_S3_Field_Prefix')"
            />
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_S3_Field_Endpoint')"
            prop="params.endpoint"
          >
            <el-input
              v-model="model.params.endpoint"
              :placeholder="$t('Integrations_S3_Field_Endpoint')"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="12">
          <form-item
            :label="$t('Integrations_S3_Field_UsePathStyleEndpoint')"
            prop="params.usePathStyleEndpoint"
          >
            <feature-toggle v-model="model.params.usePathStyleEndpoint" />
          </form-item>
        </el-col>
      </el-row>
    </form-section>

    <form-section
      v-if="isProxyEnabled"
      :title="$t('Integrations_S3_Title_OtherSettings')"
    >
      <integration-proxy-settings
        v-model="model.params.other_settings.use_static_ip"
      />
    </form-section>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import FormItem from '@/common/components/form/FormItem.vue'
import FeatureToggle from '@/common/components/form/FeatureToggle.vue'
import IntegrationProxySettings from '@/modules/integrations/components/IntegrationProxySettings.vue'
import { S3AuthenticationTypesLabels } from '@/modules/integrations/enum/AuthenticationTypesEnum'
import EnumSelect from '@/common/components/form/EnumSelect'
import store from '@/core/store'
import ElRow from '../../../../common/components/element/ElRow.vue'

export default {
  name: 'S3Settings',

  components: {
    FormSection,
    RestFormItem,
    FeatureToggle,
    IntegrationProxySettings,
    FormItem,
    ElRow,
    EnumSelect
  },

  props: {
    model: {
      type: Object,
      required: true
    }
  },

  created() {
    if (this.isCreatePage) {
      this.setDefaultOtherSettings()
    } else if (this.model.params.other_settings === undefined) {
      this.setDefaultOtherSettings()
    }
  },

  data() {
    return {
      S3AuthenticationTypesLabels,

      keyPlaceholder: '{\n    "type":"service_account",\n    ...\n}',

      rules: {
        name: {
          required: true,
          message: this.$t('RestForm_FieldIsRequired')
        },
        params: {
          type: {
            required: true
          },
          accessKey: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          secretKey: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          roleArn: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          region: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          bucket: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          }
        }
      }
    }
  },

  methods: {
    setDefaultOtherSettings() {
      this.$set(this.model.params, 'other_settings', {
        use_static_ip: 0
      })
    },
    onChangeType() {
      if (this.model.params.authType !== 'basic') {
        this.$set(this.model.params, 'accessKey', null)
        this.$set(this.model.params, 'secretKey', null)
      }
      if (this.model.params.authType !== 'assume_role') {
        this.$set(this.model.params, 'roleArn', null)
        this.$set(this.model.params, 'roleExternalId', null)
        this.$set(this.model.params, 'roleSessionName', null)
      }
    }
  },

  computed: {
    isProxyEnabled() {
      return store.state.app.proxy ? store.state.app.proxy['enabled'] : null
    },
    isCreatePage() {
      return !this.$route.query.id
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/integrations/types/s3/S3Settings.vue